import * as React from 'react'

import SafeJSON from 'modules/core/lib/SafeJSON'
import SafeLocalStorage from 'modules/core/lib/SafeLocalStorage'

import { GroupByType } from 'modules/performance_analytics/components/helpers'

type GroupByContextValue = {
  groupBy?: GroupByType
  setGroupBy: (value?: GroupByType) => void
}

const GroupByContext = React.createContext<GroupByContextValue | null>(null)

const STORAGE_KEY = 'performance-analytics-groupby'

type GroupByProviderProps = {
  children: React.ReactNode
  defaultValue?: GroupByType
}

export const GroupByProvider = ({ children, defaultValue }: GroupByProviderProps) => {
  const [groupBy, setGroupByState] = React.useState<GroupByType | undefined>(() => {
    const stored = SafeLocalStorage.getItem(STORAGE_KEY, null)
    if (stored !== null) {
      if (stored === 'undefined') {
        return undefined
      }
      const parsedValue: GroupByType = SafeJSON.parse(stored, null)
      return parsedValue !== null ? parsedValue : defaultValue
    }
    return defaultValue
  })

  const setGroupBy = React.useCallback((value?: GroupByType) => {
    setGroupByState(value)

    const serializedValue = value === undefined ? 'undefined' : JSON.stringify(value)
    SafeLocalStorage.setItem(STORAGE_KEY, serializedValue)
  }, [])

  const contextValue = React.useMemo(() => ({ groupBy, setGroupBy }), [groupBy, setGroupBy])

  return <GroupByContext.Provider value={contextValue}>{children}</GroupByContext.Provider>
}

export const useGroupBy = (): GroupByContextValue => {
  const context = React.useContext(GroupByContext)
  if (!context) {
    throw new Error('useGroupBy must be used within a GroupByProvider')
  }
  return context
}
